import { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFile,
  faFilePdf,
  faFileWord,
  faFileImage,
  faFileVideo,
  faChevronRight,
  faEllipsisV,
  faBan,
} from '@fortawesome/free-solid-svg-icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import { useUser } from '../../../contexts/UserContext';
import CustomDropdown from '../../common/CustomDropdown';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { getFileFolderPath } from '../fileFolderUtils';
import PacketGroupList from './PacketGroupList';

const getFileIcon = (file) => {
  const matches = /\.(.+)$/.exec(file.fileName);
  if (!matches) return faFile;

  switch (matches[1].toLowerCase()) {
    case 'pdf':
      return faFilePdf;
    case 'doc':
    case 'docx':
      return faFileWord;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'tiff':
    case 'gif':
      return faFileImage;
    case 'mp4':
    case 'ogg':
    case 'mpeg':
      return faFileVideo;
    default:
      return faFile;
  }
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  user-select: none;
`;

const ListItemStyled = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  background-color: ${(props) => (props.isSelected ? '#e8f0ff' : '#fff')};
  transition: background-color 0.15s;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.isSelected ? '#d9e7ff' : '#f5f5f5')};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  font-size: 0.875rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
  position: relative;
  color: #0891b2;
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ItemName = styled.div`
  font-size: 0.93rem;
  font-weight: 500;
  word-break: break-word;
  color: #0891b2;
`;

const FileDate = styled.div`
  font-size: 0.75rem;
  color: #999;
  margin-left: auto;
  margin-right: 0.5rem;
  white-space: nowrap;
`;

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.5rem;
  flex-shrink: 0;

  button {
    min-width: auto;
    height: auto;
    font-size: 0.875rem;
    color: #999;
    border: none;
    background: transparent;
    transition: all 0.15s;

    &:hover {
      color: #333;
      background-color: #e8e8e8;
      border-radius: 0.25rem;
    }

    &:active {
      background-color: #ddd;
    }
  }
`;

const ExpandIcon = styled(FontAwesomeIcon)`
  transition: transform 0.2s;
  transform: ${(props) =>
    props.$isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  color: #999;
`;

const FolderItem = ({ folder, isExpanded, onToggle, isDropTarget, isDragging }) => {
  // Disabled droppables don't fire isDropTarget, so we use isDragging instead
  // to surface the "system-managed / no-drop" affordance on packet/preview folders
  // any time a drag is in progress.
  const showBlocked = isDragging && folder.systemManaged;
  const showAllowedDropTarget = isDropTarget && !folder.systemManaged;

  let bg = 'transparent';
  if (showBlocked) bg = '#fef2f2';
  else if (showAllowedDropTarget) bg = '#e3f2fd';

  let accentColor = '#222';
  if (showBlocked) accentColor = '#b91c1c';
  else if (showAllowedDropTarget) accentColor = '#007bff';

  return (
    <ListItemStyled
      onClick={onToggle}
      style={{
        backgroundColor: bg,
        fontWeight: showAllowedDropTarget ? 600 : 400,
        boxShadow: showBlocked ? 'inset 0 0 0 1px #fecaca' : 'none',
        transition: 'background-color 0.15s, box-shadow 0.15s',
        cursor: showBlocked ? 'not-allowed' : 'pointer',
      }}
    >
      <IconWrapper style={{ marginRight: '0.75rem' }}>
        <ExpandIcon icon={faChevronRight} $isExpanded={isExpanded} />
      </IconWrapper>
      <IconWrapper style={{ marginRight: 0, marginLeft: '-0.75rem' }}>
        <FontAwesomeIcon
          icon={faFolder}
          style={{ color: accentColor }}
        />
      </IconWrapper>
      <ContentWrapper style={{ marginLeft: '0.5rem' }}>
        <ItemName style={{ color: accentColor }}>
          {folder.label}{' '}
          <span style={{ color: '#999', fontWeight: 400, fontSize: '0.85rem' }}>
            ({folder.count || 0})
          </span>
          {folder.systemManaged ? (
            <FontAwesomeIcon
              icon={faLock}
              style={{
                marginLeft: '0.5rem',
                fontSize: '0.7rem',
                color: showBlocked ? '#b91c1c' : '#9ca3af',
                verticalAlign: 'middle',
              }}
              title="System-managed folder — files are added and named automatically"
            />
          ) : null}
        </ItemName>
      </ContentWrapper>
      {showBlocked ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            marginLeft: 'auto',
            marginRight: '0.5rem',
            color: '#b91c1c',
            fontSize: '0.78rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
        >
          <FontAwesomeIcon icon={faBan} />
          <span>System-managed</span>
        </div>
      ) : null}
    </ListItemStyled>
  );
};

const FileItemComponent = ({
  file,
  isSelected,
  provided,
  snapshot,
  onFileOpen,
  onFileReplace,
  onFileRename,
  onFileDelete,
  onFileToggleLock,
  onViewParsedContent,
  onFileSelect,
  systemManaged = false,
}) => {
  const { hasPermission } = useUser();

  const isDisabled = useMemo(() => {
    if (file.fileVersion === 0) {
      return true;
    }

    if (file.fileLocked && !hasPermission('files:manage')) {
      return true;
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const menuItems = useMemo(() => {
    const list = [
      {
        label: 'Open File',
        onClick: () => onFileOpen(file),
        isDisabled: () => isDisabled,
      },
    ];

    // Destructive actions are hidden for files in system-managed folders
    // (DBQ Previews, Exam Packets) so users can't break packet integrity
    // by renaming, replacing, or deleting individual artifacts.
    if (!systemManaged) {
      list.push(
        {
          label: 'Upload New Version',
          onClick: () => onFileReplace?.(file),
          isDisabled: () => isDisabled,
        },
        {
          label: 'Rename File',
          onClick: () => onFileRename(file),
          isDisabled: () => file.fileLocked,
        },
        {
          label: 'Delete File',
          onClick: () => onFileDelete(file),
          isDisabled: () => isDisabled,
        },
      );
    }

    if (hasPermission('files:manage') && !systemManaged) {
      list.push({
        label: `${file.fileLocked ? 'Unlock' : 'Lock'} File`,
        onClick: () => onFileToggleLock(file),
      });
    }

    const canSeeParsedContent = hasPermission('admin:casegpt_content');
    if (canSeeParsedContent) {
      list.push({ divider: true });
      list.push({
        label: 'View Parsed Content',
        onClick: () => onViewParsedContent(file),
      });
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    file,
    isDisabled,
    systemManaged,
    onFileOpen,
    onFileReplace,
    onFileRename,
    onFileDelete,
    onFileToggleLock,
    onViewParsedContent,
    hasPermission,
  ]);

  const handleClick = (e) => {
    // Don't select if dragging
    if (snapshot.isDragging) return;

    // Check if the click was on the action button or dropdown menu
    if (e.target.closest('button') || e.target.closest('[class*="dropdown"]')) {
      e.stopPropagation();
      return;
    }

    // Focus the element so keyboard events work
    e.currentTarget.focus();
    onFileSelect(file);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isSelected && !file.fileLocked) {
      e.preventDefault();
      onFileRename(file);
    }
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    onFileOpen(file);
  };

  return (
    <ListItemStyled
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      data-file-item
      isSelected={isSelected}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      style={{
        ...provided.draggableProps.style,
      }}
    >
      <IconWrapper>
        <div id={`file-${file.fileId}`} style={{ position: 'relative' }}>
          <FontAwesomeIcon icon={getFileIcon(file)} />
          {file.fileLocked ? (
            <div
              style={{
                position: 'absolute',
                bottom: -5,
                right: -5,
                fontSize: '0.75rem',
                color: '#ff0000',
                backgroundColor: '#fff',
                borderRadius: '50%',
                width: '1rem',
                height: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FontAwesomeIcon icon={faLock} />
            </div>
          ) : null}
        </div>
      </IconWrapper>
      <ContentWrapper>
        <ItemName>{file.fileName}</ItemName>
      </ContentWrapper>
      {file.fileLastUpdated && (
        <FileDate>
          {/* fileLastUpdated is a plain DATE sent as UTC midnight: read it in UTC
             or it shows a day early in the US (QA 2026-10-26). */}
          {new Date(file.fileLastUpdated).toLocaleDateString('en-US', {
            timeZone: 'UTC',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </FileDate>
      )}
      <ActionWrapper>
        <CustomDropdown
          variant="link"
          drop="bottom"
          menuItems={menuItems}
          icon={faEllipsisV}
          size="sm"
        />
      </ActionWrapper>
    </ListItemStyled>
  );
};

const ListView = ({
  folders,
  files,
  onFileOpen,
  onFileReplace,
  onFileRename,
  onFileDelete,
  onFileToggleLock,
  onViewParsedContent,
  onFileMove,
}) => {
  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Deselect file when clicking anywhere outside file items
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Check if click is outside any file item
      if (!e.target.closest('[data-file-item]')) {
        setSelectedFileId(null);
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, []);

  const toggleFolder = (folderId) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const handleFileSelect = (file) => {
    setSelectedFileId(file.fileId);
  };

  const getFolderFiles = (folder) => {
    return files.filter((file) => getFileFolderPath(file, folders) === folder.path);
  };

  const getRootFiles = () => {
    return files.filter((file) => getFileFolderPath(file, folders) === 'root');
  };

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;

    // Dropped outside any droppable
    if (!destination) {
      return;
    }

    // Dropped in same location
    if (source.droppableId === destination.droppableId) {
      return;
    }

    // Find the dragged file
    const draggedFile = files.find((f) => f.fileId === draggableId);
    if (!draggedFile || !onFileMove) {
      return;
    }

    // Target is simply the droppableId (folder path or 'root')
    onFileMove(draggedFile, destination.droppableId);
  };

  const rootFiles = getRootFiles();

  const handleDragStart = () => setIsDragging(true);
  const handleDragEndWrapped = (result) => {
    setIsDragging(false);
    handleDragEnd(result);
  };

  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEndWrapped}>
      <ListContainer>
        {/* Folders */}
        {folders.map((folder, folderIdx) => {
          const isExpanded = expandedFolders[folderIdx];
          const folderFiles = getFolderFiles(folder);

          return (
            <Droppable
              key={folderIdx}
              droppableId={folder.path}
              isDropDisabled={!!folder.systemManaged}
            >
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  <FolderItem
                    folder={folder}
                    isExpanded={isExpanded}
                    onToggle={() => toggleFolder(folderIdx)}
                    isDropTarget={snapshot.isDraggingOver}
                    isDragging={isDragging}
                  />
                  {isExpanded && (
                    <div style={{ marginLeft: '1.5rem' }}>
                      {folder.groupBy === 'packet' ? (
                        <PacketGroupList
                          files={folderFiles}
                          selectedFileId={selectedFileId}
                          onFileSelect={handleFileSelect}
                          onFileOpen={onFileOpen}
                          onFileReplace={onFileReplace}
                          onFileRename={onFileRename}
                          onFileDelete={onFileDelete}
                          onFileToggleLock={onFileToggleLock}
                          onViewParsedContent={onViewParsedContent}
                          renderFileItem={(file, draggableIndex) => (
                            <Draggable
                              key={file.fileId}
                              draggableId={file.fileId}
                              index={draggableIndex}
                              isDragDisabled
                            >
                              {(itemProvided, itemSnapshot) => (
                                <FileItemComponent
                                  file={file}
                                  provided={itemProvided}
                                  snapshot={itemSnapshot}
                                  onFileSelect={handleFileSelect}
                                  onFileOpen={onFileOpen}
                                  onFileReplace={onFileReplace}
                                  onFileRename={onFileRename}
                                  onFileDelete={onFileDelete}
                                  onFileToggleLock={onFileToggleLock}
                                  onViewParsedContent={onViewParsedContent}
                                  isSelected={selectedFileId === file.fileId}
                                  systemManaged={!!folder.systemManaged}
                                />
                              )}
                            </Draggable>
                          )}
                        />
                      ) : (
                        folderFiles.map((file) => (
                          <Draggable
                            key={file.fileId}
                            draggableId={file.fileId}
                            index={0}
                            isDragDisabled={!!folder.systemManaged}
                          >
                            {(provided, snapshot) => (
                              <FileItemComponent
                                file={file}
                                provided={provided}
                                snapshot={snapshot}
                                onFileSelect={handleFileSelect}
                                onFileOpen={onFileOpen}
                                onFileReplace={onFileReplace}
                                onFileRename={onFileRename}
                                onFileDelete={onFileDelete}
                                onFileToggleLock={onFileToggleLock}
                                onViewParsedContent={onViewParsedContent}
                                isSelected={selectedFileId === file.fileId}
                                systemManaged={!!folder.systemManaged}
                              />
                            )}
                          </Draggable>
                        ))
                      )}
                    </div>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          );
        })}

        {/* Root files */}
        <Droppable droppableId="root">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                backgroundColor: snapshot.isDraggingOver
                  ? '#f0f7ff'
                  : 'transparent',
                minHeight: rootFiles.length === 0 ? '3rem' : 'auto',
              }}
            >
              {rootFiles.map((file) => (
                <Draggable
                  key={file.fileId}
                  draggableId={file.fileId}
                  index={0}
                >
                  {(provided, snapshot) => (
                    <FileItemComponent
                      file={file}
                      provided={provided}
                      snapshot={snapshot}
                      onFileSelect={handleFileSelect}
                      onFileOpen={onFileOpen}
                      onFileReplace={onFileReplace}
                      onFileRename={onFileRename}
                      onFileDelete={onFileDelete}
                      onFileToggleLock={onFileToggleLock}
                      onViewParsedContent={onViewParsedContent}
                      isSelected={selectedFileId === file.fileId}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </ListContainer>
    </DragDropContext>
  );
};

export default ListView;
